import { Mail } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { aggregatorPortalApi, type AggregatorPeopleKind } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { ImportInviteReport, ImportInviteStatus } from '../../types/api'

const STATUS_LABEL: Record<ImportInviteStatus, string> = {
  DRAFT: 'Not answered',
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
}
const STATUS_TONE: Record<ImportInviteStatus, string> = {
  DRAFT: 'bg-amber-100 text-amber-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-ink-100 text-ink-600',
}

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-'

/**
 * A coordinator's own uploads and who has been invited. An upload sends no email - everyone in it
 * stays a draft (no dot, no sign-in) until the coordinator ticks them here and presses "Send
 * invitation". The email asks them to confirm (their account goes live) or reject (it is deleted).
 */
export function UploadInvitations({
  kind,
  onError,
  reloadKey = 0,
}: {
  kind: AggregatorPeopleKind
  onError: (err: unknown, fallback: string) => string | null
  reloadKey?: number
}) {
  const [unsentOnly, setUnsentOnly] = useState(true)
  const [page, setPage] = useState(0)
  const [report, setReport] = useState<ImportInviteReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Ticked rows - unanswered ones on the page in view; cleared whenever the list reloads.
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setReport(await aggregatorPortalApi.invitations(kind, unsentOnly, page))
      setPicked(new Set())
      setError(null)
    } catch (err) {
      setError(onError(err, 'Could not load the invitations'))
    } finally {
      setLoading(false)
    }
  }, [kind, unsentOnly, page, onError])

  useEffect(() => {
    void load()
  }, [load, reloadKey])

  async function send() {
    if (picked.size === 0) return
    setSending(true)
    setNotice(null)
    setError(null)
    try {
      const { sent, skipped } = await aggregatorPortalApi.sendInvitations([...picked])
      setNotice(
        `Invitation sent to ${sent} ${sent === 1 ? 'person' : 'people'}.` +
          (skipped > 0 ? ` ${skipped} skipped - they had already answered.` : ''),
      )
      await load()
    } catch (err) {
      setError(onError(err, 'Could not send the invitations'))
    } finally {
      setSending(false)
    }
  }

  const rows = report?.rows ?? []
  const drafts = rows.filter((r) => r.status === 'DRAFT')
  const allPicked = drafts.length > 0 && drafts.every((r) => picked.has(r.id))
  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const counts = report?.counts
  const lastPage = report ? Math.max(0, Math.ceil(report.total / report.size) - 1) : 0
  const noun = kind === 'tutors' ? 'tutors' : 'learners'

  return (
    <section className="card-pad space-y-3">
      <div>
        <h2 className="font-bold text-ink-900">Send invitations</h2>
        <p className="text-xs text-ink-500">
          The {noun} you upload are not emailed automatically. Tick who should get the invitation and press Send
          invitation. Each person can confirm (their account goes live) or reject (it is deleted).
        </p>
      </div>

      {counts && (
        <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <li>
            <span className="font-bold text-red-700">{report.notEmailed}</span> <span className="text-ink-500">not emailed</span>
          </li>
          <li>
            <span className="font-bold text-amber-700">{counts.DRAFT}</span> <span className="text-ink-500">not answered</span>
          </li>
          <li>
            <span className="font-bold text-green-700">{counts.CONFIRMED}</span> <span className="text-ink-500">confirmed</span>
          </li>
          <li>
            <span className="font-bold text-ink-900">{counts.REJECTED}</span> <span className="text-ink-500">rejected</span>
          </li>
        </ul>
      )}

      <ErrorMessage message={error} />
      {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {[
            { label: 'Not emailed', value: true },
            { label: 'All uploaded', value: false },
          ].map((f) => (
            <button
              key={f.label}
              type="button"
              onClick={() => {
                setUnsentOnly(f.value)
                setPage(0)
              }}
              aria-pressed={unsentOnly === f.value}
              className={`rounded-full px-3 py-1 text-sm font-semibold ${
                unsentOnly === f.value ? 'bg-ink-900 text-ink-50' : 'bg-ink-100 text-ink-600 hover:text-ink-900'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => void send()}
          disabled={picked.size === 0 || sending}
          className="btn-sun gap-1.5 disabled:opacity-40"
        >
          <Mail size={15} />
          {sending ? 'Sending...' : `Send invitation${picked.size > 0 ? ` (${picked.size})` : ''}`}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="w-10 py-2 pr-3">
                <input
                  type="checkbox"
                  aria-label="Select everyone not answered on this page"
                  checked={allPicked}
                  disabled={drafts.length === 0}
                  onChange={() => setPicked(allPicked ? new Set() : new Set(drafts.map((r) => r.id)))}
                />
              </th>
              <th className="py-2 pr-3">Person</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Uploaded</th>
              <th className="py-2 pr-3">Email sent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-5 text-center text-ink-500">
                  {loading
                    ? 'Loading...'
                    : unsentOnly
                      ? `Everyone you uploaded has been emailed.`
                      : `You have not uploaded any ${noun} yet.`}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className={`align-top ${picked.has(r.id) ? 'bg-sun-100/50' : ''}`}>
                  <td className="py-2 pr-3">
                    {r.status === 'DRAFT' && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.fullName}`}
                        checked={picked.has(r.id)}
                        onChange={() => toggle(r.id)}
                      />
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-ink-900">{r.fullName}</div>
                    <div className="break-all text-xs text-ink-600">{r.email}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_TONE[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-ink-600">{fmt(r.uploadedAt)}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-ink-600">
                    {r.emailSentAt ? fmt(r.emailSentAt) : <span className="font-medium text-red-700">Not emailed</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {report && report.total > report.size && (
        <div className="flex items-center justify-between text-sm text-ink-600">
          <span>
            Page {page + 1} of {lastPage + 1} · {report.total} people
          </span>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <button type="button" className="btn-ghost" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
